import { useRef, useState } from 'react';
import { Button, CustomSelect } from '@/components';
import { EmailBlockToolbar } from './EmailBlockToolbar';
import { EmailCanvas } from './EmailCanvas';
import { EmailPropertyEditor } from './EmailPropertyEditor';
import { compileBlocksToEmailHtml, isEmptyRichHtml } from './emailCompiler';
import toast from 'react-hot-toast';

const CATEGORY_OPTIONS = [
  { value: 'marketing', label: 'Marketing' },
  { value: 'transactional', label: 'Transactional' },
  { value: 'notification', label: 'Notification' },
];

export function EmailBuilderContainer({
  onCancel,
  initialData,
  onSave,
  isSaving = false,
}) {
  const [blocks, setBlocks] = useState(initialData?.design_json || []);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [templateName, setTemplateName] = useState(initialData?.name || '');
  const [templateSubject, setTemplateSubject] = useState(
    initialData?.subject || '',
  );
  const [templateCategory, setTemplateCategory] = useState(
    initialData?.category || 'marketing',
  );
  const [nameError, setNameError] = useState('');
  const [previewMode, setPreviewMode] = useState(false);
  const writingRef = useRef(false);

  // Insert new blocks after selected block, or at the end if none selected, or at specific index (drag-and-drop)
  const handleAddBlock = (blockType, insertIndex = null) => {
    const newBlock = {
      id: `block-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
      type: blockType,
      content: getDefaultContentForType(blockType),
    };

    if (
      typeof insertIndex === 'number' &&
      insertIndex >= 0 &&
      insertIndex <= blocks.length
    ) {
      const newBlocks = [...blocks];
      newBlocks.splice(insertIndex, 0, newBlock);
      setBlocks(newBlocks);
      setSelectedBlockId(newBlock.id);
      return;
    }

    if (selectedBlockId) {
      const selectedIndex = blocks.findIndex((b) => b.id === selectedBlockId);
      if (selectedIndex !== -1) {
        const newBlocks = [...blocks];
        newBlocks.splice(selectedIndex + 1, 0, newBlock);
        setBlocks(newBlocks);
        setSelectedBlockId(newBlock.id);
        return;
      }
    }

    // Default: append at the end
    setBlocks([...blocks, newBlock]);
    setSelectedBlockId(newBlock.id);
  };

  const handleSelectBlock = (id) => {
    setSelectedBlockId(id);
  };

  const handleStartWriting = (index) => {
    if (writingRef.current) return;
    writingRef.current = true;
    setTimeout(() => {
      writingRef.current = false;
    }, 300);
    const at = typeof index === 'number' ? index : blocks.length;
    const reuse = at === blocks.length ? blocks[blocks.length - 1] : blocks[at];
    if (
      reuse?.type === 'TEXT' &&
      isEmptyRichHtml(reuse.content?.html) &&
      (at === blocks.length || at === blocks.indexOf(reuse))
    ) {
      setSelectedBlockId(reuse.id);
      return;
    }
    const newBlock = {
      id: `block-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
      type: 'TEXT',
      content: { ...getDefaultContentForType('TEXT'), html: '' },
    };
    const next = [...blocks];
    const insertAt = Math.min(Math.max(at, 0), next.length);
    next.splice(insertAt, 0, newBlock);
    setBlocks(next);
    setSelectedBlockId(newBlock.id);
  };

  const handleUpdateBlock = (id, newContent) => {
    setBlocks((prev) =>
      prev.map((b) => (b.id === id ? { ...b, content: newContent } : b)),
    );
  };

  const handleDeleteBlock = (id) => {
    setBlocks(blocks.filter((b) => b.id !== id));
    if (selectedBlockId === id) setSelectedBlockId(null);
  };

  const handleMoveBlock = (id, direction) => {
    const idx = blocks.findIndex((b) => b.id === id);
    if (idx < 0) return;
    if (direction === 'up' && idx > 0) {
      const newBlocks = [...blocks];
      [newBlocks[idx - 1], newBlocks[idx]] = [
        newBlocks[idx],
        newBlocks[idx - 1],
      ];
      setBlocks(newBlocks);
    } else if (direction === 'down' && idx < blocks.length - 1) {
      const newBlocks = [...blocks];
      [newBlocks[idx + 1], newBlocks[idx]] = [
        newBlocks[idx],
        newBlocks[idx + 1],
      ];
      setBlocks(newBlocks);
    }
  };

  const handleReorderBlocks = (fromIndex, toIndex) => {
    if (
      fromIndex === toIndex ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= blocks.length ||
      toIndex >= blocks.length
    ) {
      return;
    }
    const newBlocks = [...blocks];
    const [movedBlock] = newBlocks.splice(fromIndex, 1);
    newBlocks.splice(toIndex, 0, movedBlock);
    setBlocks(newBlocks);
  };

  const isDirty =
    JSON.stringify({
      blocks,
      name: templateName,
      subject: templateSubject,
      category: templateCategory,
    }) !==
    JSON.stringify({
      blocks: initialData?.design_json || [],
      name: initialData?.name || '',
      subject: initialData?.subject || '',
      category: initialData?.category || 'marketing',
    });

  const requestCancel = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard them?')) {
      return;
    }
    onCancel?.();
  };

  const handleSave = () => {
    if (!templateName.trim()) {
      setNameError('Template Name is required');
      toast.error('Template Name is required');
      return;
    }
    setNameError('');
    const design = blocks.filter(
      (block) =>
        !(
          (block.type === 'TEXT' || block.type === 'HEADING') &&
          isEmptyRichHtml(block.content?.html)
        ),
    );
    onSave({
      name: templateName.trim(),
      subject: templateSubject.trim(),
      category: templateCategory,
      variables: initialData?.variables || [],
      design_json: design,
      body: compileBlocksToEmailHtml(design),
    });
  };

  const selectedBlock = blocks.find((b) => b.id === selectedBlockId);

  return (
    <div className="email-builder flex flex-col w-full h-full min-h-0 min-w-0 flex-1 relative overflow-hidden font-sf-pro-display">
      <style>{`
        .email-builder { container-type: inline-size; }
        .email-builder-fields {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
          gap: 8px;
          align-items: center;
        }
        .email-builder-category { grid-column: 1; }
        .email-builder-preview { grid-column: 2; justify-self: end; }
        @container (min-width: 860px) {
          .email-builder-fields {
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) 180px auto;
          }
          .email-builder-category,
          .email-builder-preview { grid-column: auto; justify-self: stretch; }
        }
      `}</style>
      <div className="w-full min-w-0 bg-white border border-gray-200/90 rounded-2xl shadow-xs px-2 sm:px-4 py-2 flex items-center gap-2 shrink-0 mb-2">
        <button
          type="button"
          onClick={requestCancel}
          className="email-builder-back flex items-center justify-center w-11 h-11 rounded-[8px] bg-white border border-primary text-primary hover:bg-surface-mint transition-colors focus:outline-none shrink-0"
          aria-label="Back to Template List"
          title="Back to Template List"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.25}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>

        {/* Subtle vertical separator on desktop */}
        <div className="hidden min-[860px]:block w-px h-6 bg-gray-200 shrink-0" />

        <div className="email-builder-fields min-w-0 flex-1">
          <div className="min-w-0 h-11 px-2.5 flex items-center gap-2 rounded-xl border border-gray-200/90 bg-gray-50/50 hover:bg-white focus-within:bg-white focus-within:border-primary focus-within:ring-1 focus-within:ring-primary/20 transition-all">
            <label
              htmlFor="email-builder-template-name"
              className="text-xs font-semibold text-gray-500 whitespace-nowrap shrink-0 cursor-pointer"
            >
              Name <span className="text-red-500">*</span>
            </label>
            <input
              id="email-builder-template-name"
              type="text"
              value={templateName}
              onChange={(e) => {
                setTemplateName(e.target.value);
                if (nameError) setNameError('');
              }}
              placeholder="Template name..."
              className="w-full bg-transparent border-0 p-0 text-xs sm:text-sm font-medium text-main-black placeholder:text-gray-400 focus:outline-none focus:ring-0 min-w-0"
            />
            {nameError && (
              <span className="text-[11px] text-red-500 font-semibold whitespace-nowrap shrink-0 animate-fadeIn">
                {nameError}
              </span>
            )}
          </div>

          {/* Subject Line input (~grow) */}
          <div className="min-w-0 h-11 px-2.5 flex items-center gap-2 rounded-xl border border-gray-200/90 bg-gray-50/50 hover:bg-white focus-within:bg-white focus-within:border-primary focus-within:ring-1 focus-within:ring-primary/20 transition-all">
            <label
              htmlFor="email-builder-template-subject"
              className="text-xs font-semibold text-gray-500 whitespace-nowrap shrink-0 cursor-pointer"
            >
              Subject
            </label>
            <input
              id="email-builder-template-subject"
              type="text"
              value={templateSubject}
              onChange={(e) => setTemplateSubject(e.target.value)}
              placeholder="Subject line..."
              className="w-full bg-transparent border-0 p-0 text-xs sm:text-sm font-medium text-main-black placeholder:text-gray-400 focus:outline-none focus:ring-0 min-w-0"
            />
          </div>

          {/* Category Dropdown using common CustomSelect component */}
          <div className="email-builder-category min-w-0 h-11 px-2.5 flex items-center gap-2 rounded-xl border border-gray-200/90 bg-gray-50/50 hover:bg-white focus-within:bg-white focus-within:border-primary focus-within:ring-1 focus-within:ring-primary/20 transition-all">
            <span className="text-xs font-semibold text-gray-500 whitespace-nowrap shrink-0 select-none">
              Category
            </span>
            <div className="flex-1 min-w-0">
              <CustomSelect
                value={templateCategory}
                onChange={(val) => setTemplateCategory(val)}
                options={CATEGORY_OPTIONS}
                className="w-full h-full"
                buttonClassName="!h-[36px] w-full !bg-transparent !border-0 !p-0 text-xs sm:text-sm font-medium text-main-black capitalize focus:outline-none cursor-pointer flex items-center justify-between"
                align="left"
                dropdownWidth={180}
              />
            </div>
          </div>
          <button
            type="button"
            onClick={() => setPreviewMode((on) => !on)}
            className="email-builder-preview h-11 px-3 shrink-0 rounded-xl border border-gray-200/90 bg-white text-xs font-semibold text-gray-700 hover:text-primary hover:border-primary/50 hover:bg-surface-mint/30"
            title={previewMode ? 'Back to edit' : 'Preview template'}
            aria-pressed={previewMode}
          >
            {previewMode ? 'Edit' : 'Preview'}
          </button>
        </div>
      </div>

      {/* Canvas stays full width. Settings overlay the workspace, same as Automation. */}
      <div className="email-builder-workspace flex-1 min-h-0 min-w-0 w-full overflow-hidden h-full relative">
        <div className="email-builder-canvas h-full min-h-0 min-w-0 flex flex-col overflow-hidden">
          <EmailCanvas
            blocks={blocks}
            selectedBlockId={selectedBlockId}
            onSelectBlock={handleSelectBlock}
            onMoveBlock={handleMoveBlock}
            onDeleteBlock={handleDeleteBlock}
            onInsertBlockAt={handleAddBlock}
            onReorderBlocks={handleReorderBlocks}
            onUpdateBlock={handleUpdateBlock}
            onStartWriting={handleStartWriting}
            mode={previewMode ? 'preview' : 'edit'}
          />
        </div>

        {!previewMode && selectedBlock && (
          <>
            <div
              className="absolute inset-0 bg-black/30 z-40 sm:hidden"
              onClick={() => setSelectedBlockId(null)}
            />
            <aside
              role="dialog"
              aria-modal="true"
              aria-labelledby="email-block-settings-title"
              className="absolute z-40 flex min-w-0 max-w-full flex-col overflow-hidden inset-x-0 bottom-0 top-auto max-h-[min(88dvh,100%)] rounded-t-2xl sm:inset-y-3 sm:left-auto sm:right-3 sm:top-3 sm:max-h-none sm:w-[min(380px,calc(100%-1.5rem))] sm:rounded-none xl:inset-y-4 xl:right-4"
            >
              <EmailPropertyEditor
                selectedBlock={selectedBlock}
                onUpdateBlock={handleUpdateBlock}
                onClose={() => setSelectedBlockId(null)}
              />
            </aside>
          </>
        )}
      </div>

      <EmailBlockToolbar
        onAddBlock={handleAddBlock}
        actions={
          <>
            <Button
              variant="secondary"
              onClick={requestCancel}
              className="!h-11 !px-3 !text-xs !rounded-md"
            >
              Cancel
            </Button>
            <Button
              id="email-builder-save-btn"
              variant="primary"
              onClick={handleSave}
              disabled={isSaving}
              isLoading={isSaving}
              className="!h-11 !px-4 !text-xs !rounded-md"
            >
              Save
            </Button>
          </>
        }
      />
    </div>
  );
}

function getDefaultContentForType(type) {
  switch (type) {
    case 'HEADING':
      return {
        html: '<h2>Heading</h2>',
        align: 'left',
        color: '#1f2937',
        padding: '16px',
      };
    case 'TEXT':
      return { html: '<p>New Text Block</p>', padding: '16px' };
    case 'LOGO':
      return {
        url: '',
        alt: 'Logo',
        align: 'center',
        width: '150px',
        padding: '16px',
        link: '',
      };
    case 'IMAGE':
      return {
        url: '',
        alt: '',
        align: 'center',
        width: '100%',
        padding: '16px',
        borderRadius: '0',
        link: '',
      };
    case 'BUTTON':
      return {
        text: 'Click Here',
        url: '',
        backgroundColor: '#50AB3D',
        textColor: '#ffffff',
        align: 'center',
        borderRadius: '8px',
        padding: '16px',
        fontSize: '16px',
        fontWeight: 'bold',
      };
    case 'QUOTE':
      return {
        html: '<p><em>"Quote text goes here..."</em></p>',
        padding: '24px',
        backgroundColor: '#f9fafb',
        borderLeft: '4px solid #50AB3D',
      };
    case 'SOCIAL':
      return {
        align: 'center',
        padding: '16px',
        iconSize: '32px',
        spacing: '16px',
        networks: [
          { platform: 'facebook', url: 'https://facebook.com/yourpage' },
          { platform: 'twitter', url: 'https://x.com/yourhandle' },
          { platform: 'instagram', url: 'https://instagram.com/yourprofile' },
        ],
      };
    case 'DIVIDER':
      return { color: '#e5e7eb', height: '1px', padding: '16px' };
    case 'SPACER':
      return { height: '24px' };
    case 'FOOTER':
      return {
        html: '<p style="font-size: 12px; color: #9ca3af; text-align: center;">© 2026 {{company_name}}. All rights reserved.<br/><a href="#">Unsubscribe</a></p>',
        padding: '24px',
      };
    default:
      return {};
  }
}
